import { ApolloCache, useApolloClient } from "@apollo/client";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import Edit from "@mui/icons-material/Edit";
import { Badge, Button, Dialog, DialogContent, Divider, Theme, Typography } from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import { MealMenuLogItemWithAppearance } from "shared-ui/MealMenuLogItem/utils";
import { areServingAmountsEqual, servingAmountsToInputs } from "shared-ui/ServingAmount/utils";
import { getCommentsUnviewedNotificationIds } from "shared-ui/Comment/utils";
import { useClientTimezone } from "shared-ui/contexts/ClientTimezone";
import { formatTimeRangeInTimezone } from "@notemeal/utils-date-time";
import { ConfirmationDialog } from "apps/web/src/componentLibrary";
import DialogTitle from "apps/web/src/componentLibrary/DialogTitle";
import {
  EditTimelineMealDetailsInput,
  FullServingAmountFragment,
  TimelineMealFragment,
  TimelineMealFragmentDoc,
  useAddMealMenuLogItemMutation,
  useEditTimelineMealDetailsMutation,
  useRemoveMealMenuLogItemMutation,
  useRemoveTimelineMealServingAmountMutation,
  useSetTimelineMealServingAmountsForAthleteMutation,
} from "apps/web/src/types";
import classNames from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import { useContentStyles } from "../../../../components/MenuOrder/useContentStyles";
import FoodLogMealEditTimeModal from "../FoodLogMeal/Edit/TimeModal";
import { TimelineMealForModal, getTimelineMealInput } from "../utils";
import TimelineMealComments from "./Comments";
import LogTab from "./Log";
import TimelineMealModalOrderTab from "./OrderTab";
import TimelineMealOverview from "./Overview";
import { useTimelineMealModalContext } from "./TimelineMealModalContext";
import { LOG_TAB, ORDER_CLOSED_TAB, ORDER_TAB } from "./utils";

const SERVING_AMOUNTS_DEBOUNCE_MS = 500;


const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    title: {
      display: "flex",
      alignItems: "center",
    },
    commentBadge: {
      top: 10,
      right: -2,
    },
    paper: {
      height: `calc(100% - 64px)`,
    },
    tab: {
      padding: theme.spacing(),
      "&:hover": { cursor: "pointer" },
    },
    uppercase: { textTransform: "uppercase" },
    selectedTab: {
      borderBottomWidth: "2px",
      borderBottomColor: theme.palette.info.light,
      borderBottom: "solid",
    },
    disabledTab: {
      cursor: " not-allowed",
      "&:hover": { cursor: " not-allowed" },
      opacity: 0.5,
    },
    tabRow: {
      display: "flex",
      flexDirection: "row",
      paddingBottom: theme.spacing(3),
    },
    menu: {
      display: "flex",
      flexDirection: "column",
      flexGrow: 1,
      overflowY: "auto",
      minHeight: "100%",
    },
    backButton: {
      display: "flex",
      alignItems: "center",
      textTransform: "none",
      color: theme.palette.grey[500],
      padding: theme.spacing(0, 3),
      "&:hover": { cursor: "pointer" },
    },
    grow: { flexGrow: 1 },
    editIcon: { color: theme.palette.grey[600], marginLeft: theme.spacing(2) },
  })
);

interface TimelineMealModalProps {
  open: boolean;
  onClose: () => void;
  timelineMeal: TimelineMealForModal;
  onChangeDetails: (details: Omit<EditTimelineMealDetailsInput, "timelineMealId">) => void;
  athleteId: string;
  date: string;
  updateMealInCacheFn: (timelineMeal: TimelineMealFragment, cache: ApolloCache<any>) => void;
}

const TimelineMealModal = ({
  open,
  onClose,
  timelineMeal,
  athleteId,
  onChangeDetails,
  date,
  updateMealInCacheFn,
}: TimelineMealModalProps) => {
  const classes = useStyles();
  const contentClasses = useContentStyles();

  const {
    cartFns: { cartHasChanges },
    orderPreferencesChanged,
    currentTab,
    setCurrentTab,
    logType,
    setLogType,
    modalTabs,
  } = useTimelineMealModalContext();

  const [editTimeModalOpen, setEditTimeModalOpen] = useState(false);
  const clientTimezone = useClientTimezone();
  const timeRange = formatTimeRangeInTimezone(timelineMeal.start, timelineMeal.durationInMinutes, timelineMeal.timezone, {
    excludeTimezoneSuffix: clientTimezone === timelineMeal.timezone,
  });
  const header = `${timelineMeal.name} (${timeRange})`;

  const client = useApolloClient();

  const [unsavedChangesAlertOpen, setUnsavedChangesAlertOpen] = useState<boolean>(false);
  // Local draft so rapid adds update the list in the same React batch as autocomplete clear,
  // instead of waiting on Apollo cache broadcast (which loses races with stale props).
  const [localServingAmounts, setLocalServingAmounts] = useState(timelineMeal.servingAmounts);

  const timelineMealInput = getTimelineMealInput(timelineMeal);

  // Freshness token: bumped on every local edit so in-flight responses can't overwrite newer optimistic state.
  // Apollo Client 3.3's mutation `update`/`onCompleted` do not receive per-call context, so we track
  // generation on the mutate() promise instead. Mutations are also serialized — the server replaces the
  // full serving-amount list, so overlapping in-flight replaces can lose items on the server.
  const servingAmountsGenerationRef = useRef(0);
  const servingAmountsDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestLocalServingAmountsRef = useRef(timelineMeal.servingAmounts);
  const confirmedServingAmountsRef = useRef(timelineMeal.servingAmounts);
  const timelineMealRef = useRef(timelineMeal);
  const timelineMealInputRef = useRef(timelineMealInput);
  const athleteIdRef = useRef(athleteId);
  const persistInFlightRef = useRef(false);
  const pendingPersistGenerationRef = useRef<number | null>(null);
  const runPersistLoopRef = useRef<() => Promise<void>>(async () => {});

  timelineMealRef.current = timelineMeal;
  timelineMealInputRef.current = timelineMealInput;
  athleteIdRef.current = athleteId;

  useEffect(() => {
    setLocalServingAmounts(timelineMeal.servingAmounts);
    latestLocalServingAmountsRef.current = timelineMeal.servingAmounts;
    confirmedServingAmountsRef.current = timelineMeal.servingAmounts;
    servingAmountsGenerationRef.current = 0;
    // Reset draft when switching meals only — not on every servingAmounts cache update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timelineMeal.id]);

  const writeServingAmountsToCache = useCallback(
    (servingAmounts: readonly FullServingAmountFragment[]) => {
      client.writeFragment({
        fragment: TimelineMealFragmentDoc,
        fragmentName: "TimelineMeal",
        id: `TimelineMeal:${timelineMealRef.current.id}`,
        data: {
          ...timelineMealRef.current,
          servingAmounts,
        },
      });
    },
    [client]
  );

  const handleClose = () => {
    if (cartHasChanges() || orderPreferencesChanged) {
      setUnsavedChangesAlertOpen(true);
    } else {
      onClose();
    }
  };

  const [setTimelineMealServingAmounts] = useSetTimelineMealServingAmountsForAthleteMutation();

  const runPersistLoop = useCallback(async () => {
    if (persistInFlightRef.current) {
      return;
    }
    persistInFlightRef.current = true;
    try {
      while (pendingPersistGenerationRef.current !== null) {
        const generation = pendingPersistGenerationRef.current;
        pendingPersistGenerationRef.current = null;
        const servingAmounts = latestLocalServingAmountsRef.current;

        try {
          // no-cache: prevent Apollo from auto-merging this response over newer optimistic state
          const result = await setTimelineMealServingAmounts({
            variables: {
              input: {
                timelineMeal: timelineMealInputRef.current,
                servingAmounts: servingAmountsToInputs(servingAmounts),
                athleteId: athleteIdRef.current,
              },
            },
            fetchPolicy: "no-cache",
          });

          if (generation !== servingAmountsGenerationRef.current) {
            writeServingAmountsToCache(latestLocalServingAmountsRef.current);
          } else {
            const timelineMealResult = result.data?.setTimelineMealServingAmountsForAthlete.timelineMeal;
            if (timelineMealResult) {
              latestLocalServingAmountsRef.current = timelineMealResult.servingAmounts;
              confirmedServingAmountsRef.current = timelineMealResult.servingAmounts;
              setLocalServingAmounts(timelineMealResult.servingAmounts);
              writeServingAmountsToCache(timelineMealResult.servingAmounts);
              updateMealInCacheFn(timelineMealResult, client.cache);
            }
          }
        } catch {
          if (generation === servingAmountsGenerationRef.current) {
            latestLocalServingAmountsRef.current = confirmedServingAmountsRef.current;
            setLocalServingAmounts(confirmedServingAmountsRef.current);
            writeServingAmountsToCache(confirmedServingAmountsRef.current);
          }
        }

        // A newer edit may have arrived while this request was in flight (debounce or direct flush).
        if (
          pendingPersistGenerationRef.current === null &&
          servingAmountsGenerationRef.current !== generation &&
          !areServingAmountsEqual(latestLocalServingAmountsRef.current, confirmedServingAmountsRef.current)
        ) {
          pendingPersistGenerationRef.current = servingAmountsGenerationRef.current;
        }
      }
    } finally {
      persistInFlightRef.current = false;
      if (pendingPersistGenerationRef.current !== null) {
        void runPersistLoopRef.current();
      }
    }
  }, [client.cache, setTimelineMealServingAmounts, updateMealInCacheFn, writeServingAmountsToCache]);

  runPersistLoopRef.current = runPersistLoop;

  const persistServingAmountsToServer = useCallback(
    (generation: number) => {
      pendingPersistGenerationRef.current = generation;
      void runPersistLoop();
    },
    [runPersistLoop]
  );

  const persistServingAmountsToServerRef = useRef(persistServingAmountsToServer);
  persistServingAmountsToServerRef.current = persistServingAmountsToServer;

  const schedulePersistServingAmounts = useCallback((generation: number) => {
    if (servingAmountsDebounceTimerRef.current) {
      clearTimeout(servingAmountsDebounceTimerRef.current);
    }

    servingAmountsDebounceTimerRef.current = setTimeout(() => {
      servingAmountsDebounceTimerRef.current = null;
      persistServingAmountsToServerRef.current(generation);
    }, SERVING_AMOUNTS_DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (servingAmountsDebounceTimerRef.current) {
        clearTimeout(servingAmountsDebounceTimerRef.current);
        servingAmountsDebounceTimerRef.current = null;
        persistServingAmountsToServerRef.current(servingAmountsGenerationRef.current);
      }
    };
  }, []);

  const [removeTimelineMealServingAmount] = useRemoveTimelineMealServingAmountMutation({
    onCompleted: () => {
      confirmedServingAmountsRef.current = latestLocalServingAmountsRef.current;
    },
    onError: () => {
      latestLocalServingAmountsRef.current = confirmedServingAmountsRef.current;
      setLocalServingAmounts(confirmedServingAmountsRef.current);
      writeServingAmountsToCache(confirmedServingAmountsRef.current);
    },
  });

  const handleRemoveServingAmount = (id: string) => {
    if (servingAmountsDebounceTimerRef.current) {
      clearTimeout(servingAmountsDebounceTimerRef.current);
      servingAmountsDebounceTimerRef.current = null;
    }
    // Invalidate any in-flight set-mutation response so it can't resurrect the removed item.
    servingAmountsGenerationRef.current += 1;
    removeTimelineMealServingAmount({ variables: { input: { timelineMealId: timelineMeal.id, removeServingAmountId: id } } });
    const servingAmounts = latestLocalServingAmountsRef.current.filter(sa => sa.id !== id);
    latestLocalServingAmountsRef.current = servingAmounts;
    setLocalServingAmounts(servingAmounts);
    writeServingAmountsToCache(servingAmounts);
  };

  const handleSetServingAmounts = (servingAmounts: readonly FullServingAmountFragment[]) => {
    const generation = ++servingAmountsGenerationRef.current;
    latestLocalServingAmountsRef.current = servingAmounts;
    setLocalServingAmounts(servingAmounts);
    writeServingAmountsToCache(servingAmounts);
    schedulePersistServingAmounts(generation);
  };

  const [addMealMenuLogItem] = useAddMealMenuLogItemMutation({
    update: (cache, { data }) => {
      if (data && data.addMealMenuLogItemForAthlete.timelineMeal) {
        updateMealInCacheFn(data.addMealMenuLogItemForAthlete.timelineMeal, cache);
      }
    },
  });

  const handleAddMealMenuLogItem = ({ amount, menuItem, options }: MealMenuLogItemWithAppearance, mealMenuId: string) => {
    addMealMenuLogItem({
      variables: {
        input: {
          athleteId,
          mealMenuId,
          timelineMeal: timelineMealInput,
          item: {
            amount,
            menuItemId: menuItem.id,
            options: options.map(o => ({
              amount: o.amount,
              menuItemChoiceOptionId: o.menuItemChoiceOption.id,
            })),
          },
        },
      },
    });
  };

  const [removeMealMenuLogItem] = useRemoveMealMenuLogItemMutation({
    update: (cache, { data }) => {
      if (data && data.removeMealMenuLogItem.removedTimelineMealId) {
        cache.evict({
          id: `MealMenuLogItem:${data.removeMealMenuLogItem.mealMenuLogItemId}`,
          broadcast: true,
        });
      }
    },
  });
  const handleRemoveMealMenuLogItem = (id: string) => {
    removeMealMenuLogItem({ variables: { input: { mealMenuLogItemId: id } } });
  };

  const commentNotificationsCount = getCommentsUnviewedNotificationIds(timelineMeal.comments).length;

  const [editTimelineMealDetails] = useEditTimelineMealDetailsMutation();
  const handleEditTimelineMealDetails = async (input: Omit<EditTimelineMealDetailsInput, "timelineMealId">) => {
    if (timelineMeal.__typename === "TimelineMeal") {
      await editTimelineMealDetails({
        variables: {
          input: {
            timelineMealId: timelineMeal.id,
            ...input,
          },
        },
      });
    }
    onChangeDetails(input);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        classes={{ paper: classes.paper }}>
        <DialogTitle title={header} onClose={handleClose} />
        <DialogContent>
          <Button
            sx={{ alignSelf: "flex-start" }}
            variant="outlined"
            onClick={() => setEditTimeModalOpen(true)}
            endIcon={<Edit />}>
            Edit Meal Time
          </Button>
          <div className={contentClasses.content}>
            <div className={classes.menu}>
              <div className={classes.tabRow}>
                {modalTabs.map(modalTab => {
                  return (
                    <Badge
                      key={modalTab}
                      color="error"
                      badgeContent={modalTab === "Comment" ? commentNotificationsCount : 0}
                      classes={{
                        badge: classes.commentBadge,
                      }}
                    >
                      <div
                        key={modalTab}
                        onClick={modalTab === ORDER_CLOSED_TAB ? () => { } : () => setCurrentTab(modalTab)}
                        className={classNames([
                          classes.tab,
                          { [classes.selectedTab]: modalTab === currentTab, [classes.disabledTab]: modalTab === ORDER_CLOSED_TAB },
                        ])}
                      >
                        <Typography
                          variant="body1"
                          color={modalTab === currentTab ? "info" : "textSecondary"}
                          className={classes.uppercase}
                        >
                          {modalTab} {modalTab === "Comment" && `(${timelineMeal.comments.length})`}
                        </Typography>
                      </div>
                    </Badge>
                  );
                })}
                {currentTab === LOG_TAB && logType !== null && timelineMeal.mealMenus.length > 0 && (
                  <>
                    <div className={classes.grow} />
                    <Button
                      variant="text"
                      className={classes.backButton}
                      onClick={() => setLogType(null)}>
                      <ChevronLeft /> <Typography>Back to logging options</Typography>
                    </Button>
                  </>
                )}
              </div>

              {currentTab === ORDER_CLOSED_TAB ? (
                // The user should never see this but adding just in case
                <Typography>Ordering is Closed for all available Menu. Please use the Log Tab</Typography>
              ) : currentTab === ORDER_TAB ? (
                <TimelineMealModalOrderTab timelineMeal={timelineMeal} athleteId={athleteId} />
              ) : currentTab === LOG_TAB ? (
                <LogTab
                  timelineMeal={timelineMeal}
                  servingAmounts={localServingAmounts}
                  setServingAmounts={handleSetServingAmounts}
                  addMenuLogItem={handleAddMealMenuLogItem}
                />
              ) : (
                <TimelineMealComments
                  timelineMeal={timelineMeal}
                  athleteId={athleteId}
                  timezone={clientTimezone}
                  date={date} />
              )}
            </div>
            <Divider orientation="vertical" />
            <div className={contentClasses.order}>
              <TimelineMealOverview
                timelineMeal={timelineMeal}
                onClose={onClose}
                timezone={clientTimezone}
                date={date}
                onChangePickupTime={start =>
                  handleEditTimelineMealDetails({
                    start,
                    durationInMinutes: 30,
                    timezone: timelineMeal.timezone,
                    name: timelineMeal.name,
                    type: timelineMeal.type,
                  })
                }
                onRemoveServingAmount={handleRemoveServingAmount}
                onRemoveMealMenuLogItem={handleRemoveMealMenuLogItem}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <ConfirmationDialog
        open={unsavedChangesAlertOpen}
        title="Invalid Order"
        message="You have unsaved changes. Do you still wish to leave?"
        onCancel={() => setUnsavedChangesAlertOpen(false)}
        onConfirm={() => onClose()}
        confirmLabel="Discard"
        variant="containedDestructive"
      />
      {editTimeModalOpen && (
        <FoodLogMealEditTimeModal
          meal={timelineMeal}
          onClose={() => setEditTimeModalOpen(false)}
          open={editTimeModalOpen}
          onSave={handleEditTimelineMealDetails}
          date={date}
        />
      )}
    </>
  );
};

export default TimelineMealModal;
